import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import getOfferListKey from 'lib/offer/offerListKey'

interface ConfirmedResults {
  revision: number
  filters: Map<string, App.OfferListFilters>
}

export const SearchMapCardSearchContext = createContext<ConfirmedResults | undefined>(undefined)

interface SearchState {
  key: string
  revision: number
  expectedMapKey?: string
}

/** Keep the card's availability only for the search started by a map move. */
export function useSearchMapCardSearch(filters: App.OfferListFilters) {
  const key = getOfferListKey(filters)
  const [search, setSearch] = useState<SearchState>({ key, revision: 0 })
  const revision = search.key === key || search.expectedMapKey === key ? search.revision : search.revision + 1
  if (search.key !== key) {
    setSearch({ key, revision })
  }
  const confirmedResults = useMemo(() => ({ revision, filters: new Map<string, App.OfferListFilters>() }), [revision])

  const onMapSearch = useCallback((nextFilters: App.OfferListFilters) => {
    const expectedMapKey = getOfferListKey(nextFilters)
    setSearch(previous => ({ ...previous, expectedMapKey }))
  }, [])

  return { confirmedResults, onMapSearch }
}

export default function useSearchMapCardFilters(offerId: string, filters: App.OfferListFilters) {
  const metadata = useOfferMetaData(offerId, filters)
  const confirmedResults = useContext(SearchMapCardSearchContext)

  useEffect(() => {
    if (metadata) confirmedResults?.filters.set(offerId, filters)
  }, [metadata, offerId, confirmedResults, filters])

  // Keep the confirmed result while the map moves. Use new results when they
  // arrive, and clear the saved result when any other search setting changes.
  return (!metadata && confirmedResults?.filters.get(offerId)) || filters
}
